// import Vue from 'vue';
// import Utility from '@/vue/utility/utility';
import BaseModule from '../_base_module';

const state = {
  ...BaseModule.state,

  data: [],
};

// getters
const getters = {
  ...BaseModule.getters,
  all(state, getters, rootState, rootGetters) {
    return state.data;
  },
  /**
   * Finds a loaded news item by id.
   *
   * STRICT on id type, on purpose. `f7route.params.news_id` is a STRING and the
   * resource's `id` is a NUMBER, so the CALLER coerces:
   *
   *   this.$store.getters['news/by_id'](parseInt(this.f7route.params.news_id))
   *
   * A loose `==` here would spare the caller that line and would also silently
   * absorb a contract regression in NewsResource's `id` type (T-43-34). The
   * inherited BaseModule `find` getter does parseInt internally; this one
   * deliberately does not, so a type mismatch fails visibly rather than quietly.
   *
   * @returns {function(number): (Object|undefined)}
   */
  by_id(state, getters, rootState, rootGetters) {
    return (id) => state.data.find(item => item.id === id);
  },
};

// actions
const actions = {
  ...BaseModule.actions,
  /**
   * Fetches the member-facing news list (date-filtered, server side).
   *
   * SILENT, deliberately. A failed supplementary news panel must not put a red
   * bar over a working reservations page, and if the API is genuinely down the
   * member's own `fetch_training_slots` on the same page has already toasted —
   * so a second toast would be a duplicate about content the member did not
   * open the page for. See 43-UI-SPEC.md "The one-message rule".
   *
   * Do not delete the flag as unexplained: without it, `api/onError` toasts
   * every rejection, which is the behaviour this call site exists to opt out of.
   *
   * NOTE FOR THE CALL SITE (43-07): `silent` suppresses the toast, NOT the
   * rejection — this action still rejects. `reserveringen.vue` must attach its
   * own rejection handler, or the unhandled rejection becomes exactly the
   * console noise the one-message rule forbids.
   *
   * @returns {Promise<any> | Thenable<any> | PromiseLike<any> | *}
   */
  fetch_all ({state, commit, dispatch, getters, rootState}, payload) {
    return dispatch('api/getEndpoint', {
      endpoint: '/news',
      silent: true,
    }, {root: true}).then(response => {
      commit('set_state', {property: 'data', value: response.data.data});
    });
  },
  /**
   * Fetches every news item for the super-admin listing — no date filter, so it
   * includes scheduled, expired and inverted-range rows the member list hides.
   *
   * Deliberately NOT silent. On the admin list a load failure leaves the
   * operator with nothing to act on, so it must surface. 43-08 renders an inline
   * failure block and adds no toast of its own; the global handler's toast is
   * the only message and the two are not duplicates of each other.
   *
   * Writes to the SAME `data` property as fetch_all. That is intentional: a
   * super admin who opens /admin/news and then returns to /account/reserveringen
   * would see the admin list's contents until the member fetch re-runs — but
   * reserveringen.vue dispatches fetch_all in mounted(), so the window is a
   * single render. Two list properties in one module is more state to keep
   * coherent than the problem is worth at this size.
   *
   * @returns {Promise<any> | Thenable<any> | PromiseLike<any> | *}
   */
  fetch_all_admin ({state, commit, dispatch, getters, rootState}, payload) {
    return dispatch('api/getEndpoint', {
      endpoint: '/news/all',
    }, {root: true}).then(response => {
      commit('set_state', {property: 'data', value: response.data.data});
    });
  },
  /**
   * Resolves a single news item for the composer's edit mode.
   *
   * LOCAL RESOLUTION, not a request. There is no `GET /news/{news}` route:
   * 43-02 deliberately did not add one, because the composer can resolve its
   * item from the admin listing it already needs. So this loads that listing
   * when the store is empty and then reads the item out of it, which keeps the
   * API surface at the four routes 43-02 shipped and is honest about where the
   * data comes from.
   *
   * Does not commit the single item anywhere — `data` keeps holding the whole
   * list, so the composer and the list page do not fight over the property.
   *
   * `payload.news_id` must be a NUMBER; see the `by_id` getter.
   *
   * @returns {Promise<Object|undefined>}
   */
  find ({state, commit, dispatch, getters, rootState}, payload) {
    if (state.data.length > 0) {
      return Promise.resolve(getters.by_id(payload.news_id));
    }

    return dispatch('fetch_all_admin', {}).then(() => getters.by_id(payload.news_id));
  },
  /**
   * Stores a new news item.
   *
   * Resolves to BOTH halves of the server's envelope. The composer reloads its
   * fields from the stored, sanitised article and renders the A5 notice from the
   * report; resolving to `response.data.data` alone would silently drop the
   * report and make A5 unbuildable.
   *
   * No `.catch`, deliberately — see the note at the foot of this module.
   *
   * @returns {Promise<{item: Object, sanitisation: Object}>}
   */
  store ({state, commit, dispatch, getters, rootState}, payload) {
    return dispatch('api/postEndpoint', {
      endpoint: '/news',
      data: payload,
    }, {root: true}).then(response => {
      return {
        item: response.data.data,
        sanitisation: response.data.sanitisation,
      };
    });
  },
  /**
   * Updates an existing news item.
   *
   * Uses postEndpoint, not a PUT. The API route is `POST /news/{news}`,
   * matching every other update route in routes/api.php and the app's own
   * training_sessions/update convention.
   *
   * Same envelope as `store`, so the A5 sanitisation notice is one renderer
   * rather than two.
   *
   * @returns {Promise<{item: Object, sanitisation: Object}>}
   */
  update ({state, commit, dispatch, getters, rootState}, payload) {
    return dispatch('api/postEndpoint', {
      endpoint: '/news/' + payload.news_id,
      data: payload,
    }, {root: true}).then(response => {
      return {
        item: response.data.data,
        sanitisation: response.data.sanitisation,
      };
    });
  },
  /**
   * Destroys a news item.
   *
   * The server answers 204 with an EMPTY BODY, so this must not read
   * `response.data.data` — on a null body that throws a TypeError, turning a
   * successful delete into a failed promise.
   *
   * Removes the item locally so the list updates without a round trip. Done with
   * an explicit filter rather than BaseModule's `remove_from_data`, which
   * splices at the index returned on a miss — and that index is -1, which
   * removes the LAST element instead of nothing. A filter also lands
   * synchronously, where remove_from_data defers by 50ms.
   *
   * @returns {Promise<any> | Thenable<any> | PromiseLike<any> | *}
   */
  destroy ({state, commit, dispatch, getters, rootState}, payload) {
    return dispatch('api/deleteEndpoint', {
      endpoint: '/news/' + payload.news_id,
    }, {root: true}).then(() => {
      commit('set_state', {
        property: 'data',
        value: state.data.filter(item => item.id !== payload.news_id),
      });
    });
  },
};

// NO `.catch` ON ANY ACTION ABOVE, and that is deliberate rather than an
// oversight. `api/onError` (api.js) already renders the server's form-request
// error bag, which names the offending field — nothing the client could author
// would beat that, so a second client-side message would be a duplicate. This is
// 43-UI-SPEC.md's one-message rule, and FeedbackSheetModal.vue:98-110 is the
// in-repo precedent for the `.then`-only shape.
//
// `fetch_all` is the one exception to the toast, not to the shape: it opts out
// of the toast via the `silent` flag, and still rejects.

// mutations
const mutations = {
  ...BaseModule.mutations,
  // dummy_mutation (state, id) {
  //     state.id = id;
  // },
};

export default {

  namespaced: true,

  state,
  getters,
  actions,
  mutations
};
